import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const OrganizerSettings = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    hotline: '',
    // timezone: 'UTC',
    // currency: 'USD',
  });
  const [notifications, setNotifications] = useState({
    email_notifications: true,
    push_notifications: true,
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    new_password_confirmation: '',
  });
  const [bankDetails, setBankDetails] = useState([]);
  const [showBankModal, setShowBankModal] = useState(false);
  const [newBankDetail, setNewBankDetail] = useState({
      account_name: '',
    iban: '',
    bank_account_number: '',
    bank_account_currency: 'QAR',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadProfile();
    if (activeTab === 'bank-details') {
      loadBankDetails();
    }
  }, [activeTab]);

  const loadProfile = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/organizer/settings/profile');
      if (response.data.status === 'success') {
        setProfile({
          name: response.data.data.name || '',
          email: response.data.data.email || '',
          phone: response.data.data.phone || '',
          hotline: response.data.data.hotline || '',
          // timezone: response.data.data.timezone || 'UTC',
          // currency: response.data.data.currency || 'USD',
        });
        setNotifications({
          email_notifications: response.data.data.email_notifications,
          push_notifications: response.data.data.push_notifications,
        });
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      setError('Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSaving(true);

    try {
      const response = await api.put('/organizer/settings/profile', profile);
      if (response.data.status === 'success') {
        setSuccess('Profile updated successfully');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleNotificationsSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSaving(true);

    try {
      const response = await api.put('/organizer/settings/notifications', notifications);
      if (response.data.status === 'success') {
        setSuccess('Notification preferences updated successfully');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update preferences');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (passwordData.new_password !== passwordData.new_password_confirmation) {
      setError('New passwords do not match');
      return;
    }

    setIsSaving(true);

    try {
      const response = await api.put('/organizer/settings/password', passwordData);
      if (response.data.status === 'success') {
        setSuccess('Password changed successfully');
        setPasswordData({
          current_password: '',
          new_password: '',
          new_password_confirmation: '',
        });
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to change password');
    } finally {
      setIsSaving(false);
    }
  };

  const loadBankDetails = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/organizer/settings/bank-details');
      if (response.data.status === 'success') {
        setBankDetails(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading bank details:', error);
      setError('Failed to load bank details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddBankDetail = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSaving(true);

    try {
      const response = await api.post('/organizer/settings/bank-details', newBankDetail);
      if (response.data.status === 'success') {
        setSuccess('Bank detail added successfully');
        setShowBankModal(false);
        setNewBankDetail({
            account_name: '',
          iban: '',
          bank_account_number: '',
          bank_account_currency: 'QAR',
        });
        loadBankDetails();
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to add bank detail');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteBankDetail = async (id) => {
    if (!window.confirm('Are you sure you want to delete this bank detail?')) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      const response = await api.delete(`/organizer/settings/bank-details/${id}`);
      if (response.data.status === 'success') {
        setSuccess('Bank detail deleted successfully');
        loadBankDetails();
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to delete bank detail');
    }
  };

  const timezones = [
    'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Asia/Dubai', 'Asia/Tokyo',
    'Australia/Sydney', 'Pacific/Auckland'
  ];

  const currencies = ['USD', 'EUR', 'GBP', 'AED', 'JPY', 'AUD'];

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="text-gray-900 h5 font-weight-bold">Settings</h5>
      </div>

      {/* Success/Error Messages */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="close" onClick={() => setError('')}>
            <span>&times;</span>
          </button>
        </div>
      )}

      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {success}
          <button type="button" className="close" onClick={() => setSuccess('')}>
            <span>&times;</span>
          </button>
        </div>
      )}

      {/* Navigation Tabs */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            <i className="fas fa-user mr-2"></i>Profile
          </button>
        </li>
        {/*<li className="nav-item">*/}
        {/*  <button*/}
        {/*    className={`nav-link ${activeTab === 'notifications' ? 'active' : ''}`}*/}
        {/*    onClick={() => setActiveTab('notifications')}*/}
        {/*  >*/}
        {/*    <i className="fas fa-bell mr-2"></i>Notifications*/}
        {/*  </button>*/}
        {/*</li>*/}
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'password' ? 'active' : ''}`}
            onClick={() => setActiveTab('password')}
          >
            <i className="fas fa-lock mr-2"></i>Change Password
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'bank-details' ? 'active' : ''}`}
            onClick={() => setActiveTab('bank-details')}
          >
            <i className="fas fa-university mr-2"></i>Bank Details
          </button>
        </li>
      </ul>

      {isLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      ) : (
        <>
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="row mb-4">
              <div className="col-md-8">
                <div className="card">
                  <div className="card-header">
                    <h5 className="mb-0">Profile Information</h5>
                  </div>
                  <div className="card-body">
                    <form onSubmit={handleProfileSubmit}>
                      <div className="form-group">
                        <label htmlFor="name">Name *</label>
                        <input
                          type="text"
                          className="form-control"
                          id="name"
                          value={profile.name}
                          onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="email">Email *</label>
                        <input
                          type="email"
                          className="form-control"
                          id="email"
                          value={profile.email}
                          onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                          required
                        />
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="phone">Phone</label>
                            <input
                              type="text"
                              className="form-control"
                              id="phone"
                              value={profile.phone}
                              onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="hotline">Hotline</label>
                            <input
                              type="text"
                              className="form-control"
                              id="hotline"
                              value={profile.hotline}
                              onChange={(e) => setProfile({ ...profile, hotline: e.target.value })}
                            />
                          </div>
                        </div>
                      </div>

                      {/*<div className="row">*/}
                      {/*  <div className="col-md-6">*/}
                      {/*    <div className="form-group">*/}
                      {/*      <label htmlFor="timezone">Timezone</label>*/}
                      {/*      <select*/}
                      {/*        className="form-control"*/}
                      {/*        id="timezone"*/}
                      {/*        value={profile.timezone}*/}
                      {/*        onChange={(e) => setProfile({ ...profile, timezone: e.target.value })}*/}
                      {/*      >*/}
                      {/*        {timezones.map((tz) => (*/}
                      {/*          <option key={tz} value={tz}>*/}
                      {/*            {tz}*/}
                      {/*          </option>*/}
                      {/*        ))}*/}
                      {/*      </select>*/}
                      {/*    </div>*/}
                      {/*  </div>*/}
                      {/*  <div className="col-md-6">*/}
                      {/*    <div className="form-group">*/}
                      {/*      <label htmlFor="currency">Currency</label>*/}
                      {/*      <select*/}
                      {/*        className="form-control"*/}
                      {/*        id="currency"*/}
                      {/*        value={profile.currency}*/}
                      {/*        onChange={(e) => setProfile({ ...profile, currency: e.target.value })}*/}
                      {/*      >*/}
                      {/*        {currencies.map((curr) => (*/}
                      {/*          <option key={curr} value={curr}>*/}
                      {/*            {curr}*/}
                      {/*          </option>*/}
                      {/*        ))}*/}
                      {/*      </select>*/}
                      {/*    </div>*/}
                      {/*  </div>*/}
                      {/*</div>*/}

                      <button type="submit" className="btn !bg-yellow-400 !text-white !font-bold hover:!bg-yellow-500" disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save Profile'}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {/*{activeTab === 'notifications' && (*/}
          {/*  <div className="row mb-4">*/}
          {/*    <div className="col-md-6">*/}
          {/*      <div className="card">*/}
          {/*        <div className="card-header">*/}
          {/*          <h5 className="mb-0">Notification Preferences</h5>*/}
          {/*        </div>*/}
          {/*        <div className="card-body">*/}
          {/*          <form onSubmit={handleNotificationsSubmit}>*/}
          {/*            <div className="form-group">*/}
          {/*              <div className="custom-control custom-switch">*/}
          {/*                <input*/}
          {/*                  type="checkbox"*/}
          {/*                  className="custom-control-input"*/}
          {/*                  id="emailNotifications"*/}
          {/*                  checked={notifications.email_notifications}*/}
          {/*                  onChange={(e) =>*/}
          {/*                    setNotifications({*/}
          {/*                      ...notifications,*/}
          {/*                      email_notifications: e.target.checked,*/}
          {/*                    })*/}
          {/*                  }*/}
          {/*                />*/}
          {/*                <label className="custom-control-label" htmlFor="emailNotifications">*/}
          {/*                  Email Notifications*/}
          {/*                </label>*/}
          {/*              </div>*/}
          {/*              <small className="form-text text-muted">*/}
          {/*                Receive notifications about events, orders, and important updates via email*/}
          {/*              </small>*/}
          {/*            </div>*/}

          {/*            <div className="form-group">*/}
          {/*              <div className="custom-control custom-switch">*/}
          {/*                <input*/}
          {/*                  type="checkbox"*/}
          {/*                  className="custom-control-input"*/}
          {/*                  id="pushNotifications"*/}
          {/*                  checked={notifications.push_notifications}*/}
          {/*                  onChange={(e) =>*/}
          {/*                    setNotifications({*/}
          {/*                      ...notifications,*/}
          {/*                      push_notifications: e.target.checked,*/}
          {/*                    })*/}
          {/*                  }*/}
          {/*                />*/}
          {/*                <label className="custom-control-label" htmlFor="pushNotifications">*/}
          {/*                  Push Notifications*/}
          {/*                </label>*/}
          {/*              </div>*/}
          {/*              <small className="form-text text-muted">*/}
          {/*                Receive real-time push notifications in the application*/}
          {/*              </small>*/}
          {/*            </div>*/}

          {/*            <button type="submit" className="btn !bg-yellow-400 !text-white !font-bold hover:!bg-yellow-500" disabled={isSaving}>*/}
          {/*              {isSaving ? 'Saving...' : 'Save Preferences'}*/}
          {/*            </button>*/}
          {/*          </form>*/}
          {/*        </div>*/}
          {/*      </div>*/}
          {/*    </div>*/}
          {/*  </div>*/}
          {/*)}*/}

          {/* Password Tab */}
          {activeTab === 'password' && (
            <div className="row mb-4">
              <div className="col-md-6">
                <div className="card">
                  <div className="card-header">
                    <h5 className="mb-0">Change Password</h5>
                  </div>
                  <div className="card-body">
                    <form onSubmit={handlePasswordSubmit}>
                      <div className="form-group">
                        <label htmlFor="currentPassword">Current Password *</label>
                        <input
                          type="password"
                          className="form-control"
                          id="currentPassword"
                          value={passwordData.current_password}
                          onChange={(e) =>
                            setPasswordData({ ...passwordData, current_password: e.target.value })
                          }
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="newPassword">New Password *</label>
                        <input
                          type="password"
                          className="form-control"
                          id="newPassword"
                          value={passwordData.new_password}
                          onChange={(e) =>
                            setPasswordData({ ...passwordData, new_password: e.target.value })
                          }
                          minLength={8}
                          required
                        />
                        <small className="form-text text-muted">
                          Password must be at least 8 characters long
                        </small>
                      </div>

                      <div className="form-group">
                        <label htmlFor="confirmPassword">Confirm New Password *</label>
                        <input
                          type="password"
                          className="form-control"
                          id="confirmPassword"
                          value={passwordData.new_password_confirmation}
                          onChange={(e) =>
                            setPasswordData({
                              ...passwordData,
                              new_password_confirmation: e.target.value,
                            })
                          }
                          minLength={8}
                          required
                        />
                      </div>

                      <button type="submit" className="btn !bg-yellow-400 !text-white !font-bold hover:!bg-yellow-500" disabled={isSaving}>
                        {isSaving ? 'Changing...' : 'Change Password'}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Bank Details Tab */}
          {activeTab === 'bank-details' && (
            <div className="row mb-4">
              <div className="col-md-10">
                <div className="card">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Bank Details</h5>
                    <button
                      className="btn !bg-yellow-400 !text-white !font-bold hover:!bg-yellow-500"
                      onClick={() => setShowBankModal(true)}
                    >
                      <i className="fas fa-plus mr-2 "></i>Add New Bank Detail
                    </button>
                  </div>
                  <div className="card-body">
                    {isLoading ? (
                      <div className="text-center py-4">
                        <div className="spinner-border" role="status">
                          <span className="sr-only">Loading...</span>
                        </div>
                      </div>
                    ) : bankDetails.length === 0 ? (
                      <div className="text-center py-5">
                        <i className="fas fa-university fa-3x text-muted mb-3"></i>
                        <p className="text-muted">No bank details added yet</p>
                        <button
                          className="btn !bg-yellow-400 !text-white !font-bold hover:!bg-yellow-500"
                          onClick={() => setShowBankModal(true)}
                        >
                          <i className="fas fa-plus mr-2"></i>Add Your First Bank Detail
                        </button>
                      </div>
                    ) : (
                      <div className="bank-details-list">
                        {bankDetails.map((detail, index) => (
                          <div key={detail.id} className="card mb-3 border">
                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-start">
                                <div className="flex-grow-1">
                                  <h6 className="mb-3">
                                    <i className="fas fa-university text-yellow-400 mr-2"></i>
                                    Bank Account #{index + 1}
                                  </h6>
                                  <div className="row">
                                      <div className="col-md-6 mb-2">
                                          <small className="text-muted d-block">Account Name</small>
                                          <strong>{detail.account_name}</strong>
                                      </div>
                                    <div className="col-md-6 mb-2">
                                      <small className="text-muted d-block">IBAN</small>
                                      <strong>{detail.iban}</strong>
                                    </div>
                                    <div className="col-md-6 mb-2">
                                      <small className="text-muted d-block">Account Number</small>
                                      <strong>{detail.bank_account_number}</strong>
                                    </div>
                                    <div className="col-md-6 mb-2">
                                      <small className="text-muted d-block">Currency</small>
                                      <strong>{detail.bank_account_currency}</strong>
                                    </div>
                                    <div className="col-md-6 mb-2">
                                      <small className="text-muted d-block">Added On</small>
                                      <strong>{new Date(detail.created_at).toLocaleDateString()}</strong>
                                    </div>
                                  </div>
                                </div>
                                <button
                                  className="btn btn-danger btn-sm ml-3"
                                  onClick={() => handleDeleteBankDetail(detail.id)}
                                  title="Delete bank detail"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Add Bank Detail Modal */}
      {showBankModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New Bank Detail</h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => setShowBankModal(false)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <form onSubmit={handleAddBankDetail}>
                <div className="modal-body">
                    <div className="form-group">
                        <label htmlFor="account_name">Account Name *</label>
                        <input
                            type="text"
                            className="form-control"
                            id="account_name"
                            value={newBankDetail.account_name}
                            onChange={(e) => setNewBankDetail({ ...newBankDetail, account_name: e.target.value })}
                            placeholder="Enter Account Name"
                            required
                        />
                    </div>
                  <div className="form-group">
                    <label htmlFor="iban">IBAN *</label>
                    <input
                      type="text"
                      className="form-control"
                      id="iban"
                      value={newBankDetail.iban}
                      onChange={(e) => setNewBankDetail({ ...newBankDetail, iban: e.target.value })}
                      placeholder="Enter IBAN"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="bank_account_number">Bank Account Number *</label>
                    <input
                      type="text"
                      className="form-control"
                      id="bank_account_number"
                      value={newBankDetail.bank_account_number}
                      onChange={(e) => setNewBankDetail({ ...newBankDetail, bank_account_number: e.target.value })}
                      placeholder="Enter account number"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="bank_account_currency">Currency *</label>
                    <select
                      className="form-control"
                      id="bank_account_currency"
                      value={newBankDetail.bank_account_currency}
                      onChange={(e) => setNewBankDetail({ ...newBankDetail, bank_account_currency: e.target.value })}
                      required
                    >
                      <option value="QAR">QAR - Qatari Riyal</option>
                      <option value="AED">AED - UAE Dirham</option>
                      <option value="SAR">SAR - Saudi Riyal</option>
                      <option value="USD">USD - US Dollar</option>
                      <option value="EUR">EUR - Euro</option>
                      <option value="GBP">GBP - British Pound</option>
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="submit"
                    className="btn !bg-yellow-400 !text-white !font-bold hover:!bg-yellow-500"
                    disabled={isSaving}
                  >
                    {isSaving ? 'Adding...' : 'Add Bank Detail'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizerSettings;
